import Controller, { OnChange, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangePatientConfirmationAliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';
import InformationBarUtils from '../commons/header/information-bar-header-utils';
import { BrokerService, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { ButtonAPI, FieldAPI, PanelAPI, TextAPI, TimeFieldAPI } from '@philips-internal/oswald-ui-components';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Endpoint } from '../../constants/endpoints';
import moment from 'moment';
import { ToastMsgUtils } from '../../utils/toastmsg-utils';
import { changePatientData } from '../../interfaces/change-patient/change-patient-data';

@Controller({ component: ChangePatientConfirmationAliases.LAYOUT_MAIN, fragment: FragmentNames.CHANGE_PATIENT_CONFIRMATION })
class ChangePatientConfirmationController extends BaseController {
  @Inject(ChangePatientConfirmationAliases.TEXT_ATENDIMENTO)
  private readonly encounterId!: TextAPI;

  @Inject(ChangePatientConfirmationAliases.TEXT_DESTINO)
  private readonly destination!: TextAPI;

  @Inject(ChangePatientConfirmationAliases.PANEL_LOADER)
  private readonly panelLoaderApi!: PanelAPI;

  @Inject(ChangePatientConfirmationAliases.FIELD_PERMANENCIA)
  private readonly fieldPermanencia!: FieldAPI;

  @Inject(ChangePatientConfirmationAliases.TIME_FIELD_0)
  private readonly timeField0!: TimeFieldAPI;

  @Inject(ChangePatientConfirmationAliases.BUTTON_CONFIRMAR)
  private readonly buttonConfirm!: ButtonAPI;

  @OnClick({ target: ChangePatientConfirmationAliases.BUTTON_CANCELAR })
  onClickCancel(): void {
    applicationContext.popNavigationItem();
  }

  @OnClick({ target: ChangePatientConfirmationAliases.BUTTON_CONFIRMAR })
  async onClickConfirm(): Promise<void> {
    const nrSeqLocalPa: number = this.context.get('changeData').nrSequencia as number;
    const { encounterId } = this.historyService.getCurrentRouteParams() as Record<string, number>;
    const isPatientDisplacement = changePatientData.isPatientDisplacement;
    if (isPatientDisplacement) {
      const barCode = this.context.get('changeData').cdBarras as string;
      const procParams = {
        encounterId: this.encounterId.getValue(),
        nrSeqLocalPa: barCode
      }
      try {
        const resp = await HttpService.get(Endpoint.PALM_GENERATE_MOVEMENT_PA, procParams);
        if (resp.ok) {
          ToastMsgUtils.toastSucessMessage('msg.success', 'text.location_change_success');
        } else {
          ToastMsgUtils.toastErrorMessage('text.message-alert-attention', 'text.location_change_failed');
        }
      } catch (err) {
        ToastMsgUtils.toastErrorMessage('tws.error-generic', 'text.contact_system_admin');
      } finally {
        applicationContext.popNavigationItem();
      }
    } else {
      let minutes = 0;
      if (this.fieldPermanencia.isVisible()) {
        const value = this.timeField0.getValue() as string;
        const localTime = moment.utc(value, 'HH:mmZ').local();
        minutes = localTime.hours() * 60 + localTime.minutes();
      }
      this.panelLoaderApi.setVisible(true);
      const params = {
        encounterId,
        nrSeqLocalPa,
        time: minutes,
      };
      const response = await HttpService.get(Endpoint.CHANGE_LOCATION_PA, params);
      if (response.ok) {
        ToastMsgUtils.toastSucessMessage('msg.success', 'text.location_change_success');
        setTimeout(() => {
          this.goBack();
        }
        , 1000);
      } else {
        ToastMsgUtils.toastErrorMessage('text.message-alert-attention', 'text.change_location_error');
        this.panelLoaderApi.setVisible(false);
      }
    }
  }

  @OnMount({ target: ChangePatientConfirmationAliases.LAYOUT_MAIN })
  onMountInput(): void {
    this.mountInformationBar();
  }

  @OnMount({ target: ChangePatientConfirmationAliases.FORM_CONFIRMATION })
  onMountLayout(): void {
    this.inputFieldValues();
  }

  @OnMount({ target: ChangePatientConfirmationAliases.BUTTON_CONFIRMAR })
  onMountButtonConfirm(): void {
    if (this.fieldPermanencia.isVisible()) {
      this.buttonConfirm.setEnabled(false);
    }
  }

  @OnChange({ target: ChangePatientConfirmationAliases.FIELD_PERMANENCIA })
  onChangeFieldPermanencia(): void {
    const isPatientDisplacement = changePatientData.isPatientDisplacement;
    if (isPatientDisplacement) {
      return;
    }
    const value = this.timeField0.getValue() as string;
    const localTime = moment.utc(value, 'HH:mmZ').local();
    const minutes = localTime.hours() * 60 + localTime.minutes();
    if (minutes > 0) {
      this.buttonConfirm.setEnabled(true);
    } else {
      ToastMsgUtils.toastErrorMessage('text.message-alert-attention', 'text.location_needs_duration');
      this.buttonConfirm.setEnabled(false);
    }
  }

  private inputFieldValues(): void {
    const isPatientDisplacement = changePatientData.isPatientDisplacement;
    BrokerService.publishToTopic('setInformationBarData', this.context);
    BrokerService.publishToTopic('setChangeData', this.context);
    if (this.context.get('changeData').ieTempo === 'N' || isPatientDisplacement) {
      this.fieldPermanencia.setVisible(false);
    }
    const { encounterId } =
      this.historyService.getCurrentRouteParams() as Record<string, string>;
    this.destination.setValue(this.context.get('changeData').dsLocal as string);
    this.encounterId.setValue(encounterId);
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private mountInformationBar(): void {
    const { patientId, encounterId } =
      this.historyService.getCurrentRouteParams();

    InformationBarUtils.collapsedBar = true;
    InformationBarUtils.allowInfo = true;

    this.context.set({
      encounterId,
      patientId,
    });
  }
}
export default ChangePatientConfirmationController;
